import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Link as MUILink,
  Menu,
  MenuItem,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { Link, useLocation } from 'react-router-dom'

import useTrackingLine from '../../context/useTrackingLine'
import useTranslations from '../../i18n/useTranslations'

import TrackingLine from '../../ui/modules/TrackingLine'

import { useFullTracking } from '../../services/shippingsTracking'

import { ReactComponent as CaretDown } from '../../assets/icons/caret-down.svg'
import { ReactComponent as DotsThreeVertical } from '../../assets/icons/dots-three-vertical.svg'
import { ReactComponent as FilePdf } from '../../assets/icons/file-pdf.svg'
import { ReactComponent as FileExcel } from '../../assets/icons/file-excel.svg'

import EnhancedTableHead from './EnhancedTableHead'
import EnhancedTableToolbar from './EnhancedTableToolbar'
import DateDigest from './DateDigest'
import StatusCodeChip from '../../ui/components/chip/StatusCodeChip'
import { format, parseISO } from 'date-fns'
import ActivateReturnDrawer from '../ShippingsTracking/ActivateReturnDrawer'
import ScheduledPickupDrawer from '../../ui/modules/ScheduledPickupDrawer'
import { SHIPPING_STATUS_CODES } from '../../common/constants'

import styles from './styles.js'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

//TODO: REFACTOR TO AVOID THE NECESSITY OF RECEIVING PAGING PARAMS
//THIS SHOULD BE OPTIONAL
const DEFAULT_PAGING_PARAMS = {
  page: 0,
  rowsPerPage: 10,
  totalRows: 28,
}

const ShippingsOverviewView = ({
  headCells,
  headCellsOrder,
  idHolder = 'id',
  onChangeTableParams,
  defaultSortType = 'desc',
  onClickExpand,
  orderLineId = 'orderLineId',
  states,
  rows,
  showIcon,
  showMenu,
  showPagination,
  showCheckbox,
  collapsableHeader,
  shippingsType,
  tableParams = DEFAULT_PAGING_PARAMS,
  rowsPerPageOptions = [10, 25, 50],
  onCheckboxChange = () => {},
  getActionsForRow,
  onSelectAllClick = () => {},
  onClickResolveIncident,
  selectedRows = [],
  isBill,
  statusList,
  changeSelectedTracking,
}) => {
  const t = useTranslations()
  const { pathname } = useLocation()
  const { page, rowsPerPage, totalRows } = tableParams
  const [selected, setSelected] = useState([])
  const [rowExpanded, setRowExpanded] = useState(null)
  const [anchorEls, setAnchorEls] = useState(null)
  const anchorRefs = useRef([])
  const [dependencyData, setDependencyData] = useState(null)
  const [trackingData, setTrackingData] = useState(null)

  const {
    getTrackingData,
    activateReturnShippingInfo,
    cancelActivateReturn,
    scheduledPickupShippingsInfo,
    cancelScheduledPickup,
  } = useTrackingLine(statusList, onClickResolveIncident, dependencyData)

  const [shippingCode, setShippingCode] = useState(null)
  const [shipping, setShipping] = useState(null)
  const { data, isLoading, refetch } = useFullTracking(shippingCode)
  const [isCollapsed, setIsCollapsed] = useState(
    collapsableHeader?.title && collapsableHeader?.subtitle ? true : false,
  )

  useEffect(() => {
    if (!isLoading) {
      setDependencyData(data)
    }
  }, [data, isLoading])

  useEffect(() => {
    if (dependencyData) {
      const fetchedTrackingData = getTrackingData(shipping)
      setTrackingData(fetchedTrackingData)

      if (fetchedTrackingData) {
        onClickExpand && onClickExpand(shippingCode)
        setRowExpanded(shippingCode)
      }
    }
  }, [dependencyData, shipping, shippingCode])

  const handleClickExpanded = row => {
    if (rowExpanded !== row.info.shippingCode) {
      setShippingCode(row.info.shippingCode)
      setShipping(row)
      refetch()
    } else {
      setRowExpanded(null)
      setShippingCode(null)
    }
  }

  const handleClick = id => {
    setAnchorEls(id)
  }

  const handleClose = id => {
    setAnchorEls(null)
  }

  const showLabelDisplayedRows = () => {
    if (!totalRows) return null
    const indexInitRow = (page + 1) * rowsPerPage - rowsPerPage + 1
    const indexLastRow = Math.min(indexInitRow + rowsPerPage - 1, totalRows)
    return `${indexInitRow}-${indexLastRow} de ${totalRows}`
  }

  const handleRequestSort = (event, sortColumn) => {
    const getOppositeSortType = sortType =>
      sortType === 'asc' ? 'desc' : 'asc'

    onChangeTableParams({
      ...tableParams,
      sortColumn,
      sortType:
        sortColumn === tableParams.sortColumn
          ? getOppositeSortType(tableParams.sortType)
          : defaultSortType,
    })
  }

  const handleSelectAllClick = event => {
    const rowIds = rows.map(n => n.info[orderLineId] ?? n.info[idHolder])
    let nextSelected = selected.filter(selected => !rowIds.includes(selected))
    if (event.target.checked) {
      nextSelected = nextSelected.concat(rowIds)
    }
    setSelected(nextSelected)
    onSelectAllClick(!event.target.checked, rows)
  }
  // eslint-disable-next-line
  const handleChangePage = (event, newPage) => {
    onChangeTableParams({ ...tableParams, page: newPage })
  }

  const handleChangeRowsPerPage = event => {
    onChangeTableParams({
      ...tableParams,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    })
  }
  // const isSelected = id => selected.includes(id)

  // const selectedHere = rows
  //   .map(row => row.info?.shippingCode ?? row.info?.identificador_identidad)
  //   .filter(id => selected.includes(id))

  const renderCollapsableHeader = () => {
    return collapsableHeader?.title && collapsableHeader?.subtitle ? (
      <Box
        sx={styles.tableHeadCollapse}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <Box sx={styles.tableHeadCollapseIcon}>{collapsableHeader.icon}</Box>
        <Box>
          <Typography variant="subtitle1">{collapsableHeader.title}</Typography>
          <Typography sx={styles.tableHeadCollapseTitle}>
            {collapsableHeader.subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="expand"
          onClick={() => setIsCollapsed(!isCollapsed)}
          sx={styles.tableHeadCollapseButtonIcon}
          className={isCollapsed ? 'is-collapsed' : null}
        >
          <CaretDown />
        </IconButton>
      </Box>
    ) : null
  }

  return (
    <>
      {renderCollapsableHeader()}
      <TableContainer sx={{ ...styles.tableCollapse, mb: 2 }}>
        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
          <MuiTable aria-labelledby="tableTitle" aria-label="enhanced table">
            <EnhancedTableHead
              headCells={headCells}
              numSelected={selectedRows.length}
              order={tableParams.sortType}
              orderBy={tableParams.sortColumn}
              showCheckbox={showCheckbox}
              showIcon={showIcon}
              showMenu={showMenu}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              rows={rows}
              selectedRows={selectedRows}
            />

            <TableBody>
              {stableSort(
                rows,
                getComparator(tableParams.sortType, tableParams.sortColumn),
              ).map((row, index) => {
                const id = row.info?.shippingCode
                const stateForThisRow = states?.find(
                  state => state.shippingCode === id,
                )
                row.info.lastShippingStatusCode = stateForThisRow
                const rowInfo = row.info
                const selectedRow = !!selectedRows.find(
                  ({ shippingCode }) => shippingCode === rowInfo?.shippingCode,
                )

                const hasIncident =
                  stateForThisRow && stateForThisRow.incidentCode
                const isCancelled = row.info?.isCancelled === true

                const rowActions =
                  showMenu && getActionsForRow !== undefined
                    ? Object.entries(
                        getActionsForRow(row.info, shippingsType),
                      ).filter(action => action[1].isActive)
                    : []
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={index}>
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={id}
                      className={clsx(row.details?.severity, {
                        'is-selected': selectedRow,
                      })}
                    >
                      {showCheckbox ? (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRow}
                            onChange={e =>
                              onCheckboxChange(rowInfo, stateForThisRow)
                            }
                          />
                        </TableCell>
                      ) : null}

                      {headCellsOrder?.map((key, index) =>
                        index === 0 ? (
                          <TableCell key={key} className="main">
                            <MUILink
                              underline="none"
                              className="linkCode"
                              component={Link}
                              to={{
                                pathname: `/shippings-tracking/${id}`,
                                state: { from: pathname },
                              }}
                            >
                              {row.info[key]}
                            </MUILink>
                          </TableCell>
                        ) : key === 'shippingStatusCode' ? (
                          // TODO: Refactor
                          <TableCell key={key}>
                            <StatusCodeChip
                              id={id}
                              size="small"
                              hasIncident={hasIncident}
                              isCancelled={isCancelled}
                              state={stateForThisRow}
                              isRequestedReturn={
                                rowInfo?.relatedReturn
                                  ?.reverseReturnShippingCode
                              }
                              isUnscheduledPickup={
                                row.info.pickupUnscheduled &&
                                stateForThisRow.lastStatusCode ===
                                  SHIPPING_STATUS_CODES.manifested
                              }
                              className="has-cursorPointer"
                            />
                          </TableCell>
                        ) : key === 'manifestDate' ? (
                          // TODO: Refactor
                          <TableCell key={key}>
                            {format(parseISO(row.info[key]), 'dd/MM/yyyy')}
                          </TableCell>
                        ) : key === 'senderAddress2' ||
                          key === 'destinAddress2' ||
                          key === 'recipientName' ||
                          key === 'clientReference' ? (
                          <TableCell
                            key={key}
                            className={`truncate ${
                              key === 'clientReference' ? 'truncateXS' : null
                            }`}
                          >
                            <Tooltip
                              title={
                                <Typography variant="body2">
                                  {row.info[key]}
                                </Typography>
                              }
                              placement="top-start"
                            >
                              <Box component="span">{row.info[key]}</Box>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell key={key}>{row.info[key]}</TableCell>
                        ),
                      )}

                      {isBill ? (
                        <>
                          <TableCell classes={{ root: 'icon' }}>
                            <IconButton>
                              <FilePdf />
                            </IconButton>
                          </TableCell>
                          <TableCell classes={{ root: 'icon' }}>
                            <IconButton>
                              <FileExcel />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : null}

                      {showIcon ? (
                        <TableCell classes={{ root: 'icon' }}>
                          <IconButton
                            aria-label="expand"
                            onClick={() => handleClickExpanded(row)}
                            className={clsx(row.details?.severity, {
                              'is-expanded': rowExpanded === id,
                            })}
                          >
                            <CaretDown />
                          </IconButton>
                        </TableCell>
                      ) : null}
                      {showMenu ? (
                        <TableCell classes={{ root: 'icon' }}>
                          {rowActions.length > 0 ? (
                            <>
                              <IconButton
                                aria-label="more"
                                size="large"
                                id="contextual-button"
                                aria-controls="contextual-menu"
                                aria-haspopup="true"
                                onClick={() => handleClick(id)}
                                ref={el => (anchorRefs.current[id] = el)}
                              >
                                <DotsThreeVertical />
                              </IconButton>
                              <Menu
                                id={id}
                                anchorEl={anchorRefs.current[id]}
                                keepMounted
                                open={Boolean(anchorEls === id)}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'center',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'center',
                                }}
                                onClose={handleClose}
                                onClick={handleClose}
                              >
                                {rowActions.map(
                                  ([actionType, actionConfig]) => (
                                    <MenuItem
                                      key={actionType}
                                      onClick={actionConfig.onClick}
                                    >
                                      {actionConfig.label}
                                    </MenuItem>
                                  ),
                                )}
                              </Menu>
                            </>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {row.details ? (
                      <TableRow>
                        <TableCell
                          classes={{ root: 'subtableWrapper' }}
                          // TODO Revisar la lógica
                          colSpan={headCells.length + 4}
                        >
                          <Collapse
                            in={rowExpanded === id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box display="flex" justifyContent="space-between">
                              <TrackingLine
                                {...trackingData}
                                shippingInfo={rowInfo}
                                isCancelled={rowInfo?.isCancelled}
                                shippingStatusCode={
                                  row.info.lastShippingStatusCode
                                    ?.lastStatusCode ??
                                  rowInfo?.shippingStatusCode?.code
                                }
                              />
                              <DateDigest
                                deliveryDate={rowInfo.committedDeliveryDatetime}
                                pickupUnscheduled={rowInfo.pickupUnscheduled}
                                deliveredDate={
                                  rowInfo.shippingStatusCode?.code ===
                                  SHIPPING_STATUS_CODES.delivered
                                    ? rowInfo.shippingStatusDatetime
                                    : null
                                }
                                hasIncident={Boolean(rowInfo.incident.code)}
                                returnShippingCode={
                                  rowInfo.relatedReturn?.returnShippingCode ||
                                  rowInfo.relatedReturn
                                    .reverseReturnShippingCode
                                }
                                cancellationDatetime={
                                  rowInfo.isCancelled
                                    ? rowInfo.cancellationDatetime
                                    : null
                                }
                                shippingInfo={rowInfo}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </MuiTable>
        </Collapse>
      </TableContainer>
      {showPagination ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          px={1}
        >
          <EnhancedTableToolbar
            numTotal={totalRows}
            numSelected={selected.length}
          />
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage={t('noun:shipments-per-page')}
            labelDisplayedRows={showLabelDisplayedRows}
            count={totalRows ?? 1}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      ) : null}

      <ActivateReturnDrawer
        activateReturnShippingInfo={activateReturnShippingInfo}
        onClickClose={cancelActivateReturn}
        onClickShowReturnsList={changeSelectedTracking}
      />

      <ScheduledPickupDrawer
        shippingsInfo={scheduledPickupShippingsInfo}
        onClickClose={cancelScheduledPickup}
      />
    </>
  )
}

ShippingsOverviewView.propTypes = {
  showCheckbox: PropTypes.bool,
  showIcon: PropTypes.bool,
  showMenu: PropTypes.bool,
  collapsableHeader: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    icon: PropTypes.element,
  }),
  shippingsType: PropTypes.string,
  isBill: PropTypes.bool,
  statusList: PropTypes.array,
  showPagination: PropTypes.bool,
  headCells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      sortable: PropTypes.bool,
      extraClass: PropTypes.string,
    }),
  ),
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  headCellsOrder: PropTypes.arrayOf(PropTypes.string),
  states: PropTypes.array,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      info: PropTypes.oneOfType([
        PropTypes.shape({
          numero_colegiado: PropTypes.string,
          nombre: PropTypes.string,
          apellidos: PropTypes.string,
          modalidades: PropTypes.string,
          farmacia: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
        }),
        PropTypes.shape({
          nombre: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
          estado: PropTypes.string,
        }),
        PropTypes.shape({
          year: PropTypes.string,
          day: PropTypes.string,
          reason: PropTypes.string,
          type: PropTypes.string,
          locality: PropTypes.string,
        }),
      ]),
    }),
  ),
  tableParams: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalRows: PropTypes.number,
    sortType: PropTypes.string,
    sortColumn: PropTypes.string,
  }),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.string),
  onChangeTableParams: PropTypes.func,
  defaultSortType: PropTypes.oneOf(['desc', 'asc']),
  onClickExpand: PropTypes.func,
  idHolder: PropTypes.string,
  orderLineId: PropTypes.string,
  onCheckboxChange: PropTypes.func,
  getActionsForRow: PropTypes.func,
  onSelectAllClick: PropTypes.func,
  onClickResolveIncident: PropTypes.func,
  changeSelectedTracking: PropTypes.func,
}

export default ShippingsOverviewView
